import React from 'react';
import { Outlet } from 'react-router-dom';

import { CardDefinitionsProvider } from './contexts/CardDefinitionsContext';
import { CollectionsProvider } from './contexts/CollectionsContext';
import { ShortcutsProvider } from './contexts/ShortcutsContext';
import { CurrentUserProvider } from './contexts/CurrentUserContext';
import { PreferencesProvider } from './contexts/PreferencesContext';
import { ThemeSync } from './contexts/ThemeContext';
import { FavoritesProvider } from './contexts/FavoritesContext';
import { NotificationsProvider } from './contexts/NotificationsContext';
import { UnsavedChangesProvider } from './contexts/UnsavedChangesContext';
import { TagsProvider } from './contexts/TagsContext';
import RequestPauseNotice from './components/common/RequestPauseNotice';
import TourProvider from './components/tour/TourProvider';
import { WelcomeTourProvider } from './contexts/WelcomeTourContext';

/**
 * The root route's element: the provider stack, then whichever route
 * matched. It is a route element (not a wrapper around the router) because
 * the app runs on a data router — `createBrowserRouter` + `RouterProvider`
 * in src/index.js — and a data router's hooks (`useBlocker`, and the
 * `useLocation` several providers read) only work below `RouterProvider`.
 * The root route has no path, so it matches every URL and stays mounted
 * across navigations: none of these providers remount on a link click.
 *
 * ThemeProvider is the one level above the router (src/index.js): it renders
 * the antd ConfigProvider and App, so the palette is in place before any antd
 * component mounts, and the unsaved-changes confirm below is themed.
 */
export default function AppRoot() {
	return (
		<PreferencesProvider>
			{/* Feeds the saved preferences into the ThemeProvider above. */}
			<ThemeSync />
			{/* The one "pausing for a moment" notice while the api client's
			    governor holds new reads (src/api/governor.js). */}
			<RequestPauseNotice />
			<CurrentUserProvider>
				{/* Inside CurrentUserProvider on purpose: the inbox poll is
				    gated on there being a signed-in user. */}
				<NotificationsProvider>
					<FavoritesProvider>
						<CardDefinitionsProvider>
							{/* The collections the person can read — one fetch a session,
							    shared by the rail, palette, switcher and pickers. */}
							<CollectionsProvider>
								{/* My private tags — lazy: asked only when a
								    surface that shows them mounts. */}
								<TagsProvider>
									{/* The keyboard-shortcut register every page's ⋯
									    opens. Screens declare their shortcuts as they
									    mount. */}
									<ShortcutsProvider>
										{/* The one router blocker every dirty form
										    registers with (hooks/useDirtyForm.js). */}
										<UnsavedChangesProvider>
											{/* The one place a product tour starts
											    (components/tour). Inert until a
											    caller starts one; saves nothing. */}
											<TourProvider>
												{/* The welcome tour's caller: reads the
												    person's tour progress once at
												    sign-in and starts the tour for
												    Home's offer and the account menu. */}
												<WelcomeTourProvider>
													<Outlet />
												</WelcomeTourProvider>
											</TourProvider>
										</UnsavedChangesProvider>
									</ShortcutsProvider>
								</TagsProvider>
							</CollectionsProvider>
						</CardDefinitionsProvider>
					</FavoritesProvider>
				</NotificationsProvider>
			</CurrentUserProvider>
		</PreferencesProvider>
	);
}
